import {
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { GlobalContext } from "../Context/GlobalContext";

const MODULE_ORDER = [
  "Dashboard",
  "Whatsapp",
  "SMS",
  "Email",
  "Orders",
  "Inventory",
  "Developers",
  "Settings",
  "Billing",
  "Pos",
  "Support",
];

const sortModules = (apiModules = []) => {
  if (!Array.isArray(apiModules)) return [];

  return [...apiModules].sort((a, b) => {
    const aModuleName = String(a?.module_name || "")
      .trim()
      .toLowerCase();

    const bModuleName = String(b?.module_name || "")
      .trim()
      .toLowerCase();

    const aIndex = MODULE_ORDER.findIndex(
      (name) => name.toLowerCase() === aModuleName,
    );

    const bIndex = MODULE_ORDER.findIndex(
      (name) => name.toLowerCase() === bModuleName,
    );

    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });
};

const getCachedModules = (userid) => {
  if (!userid) return [];

  const cacheKey = `entitlement_${userid}`;

  try {
    const cachedData = sessionStorage.getItem(cacheKey);

    if (!cachedData) return [];

    const parsedData = JSON.parse(cachedData);

    if (!Array.isArray(parsedData)) {
      sessionStorage.removeItem(cacheKey);
      return [];
    }

    return sortModules(parsedData);
  } catch (error) {
    console.error("Entitlement cache parse error:", error);

    sessionStorage.removeItem(cacheKey);

    return [];
  }
};

const useSideNav = (passedUserID) => {
  const contextData = useContext(GlobalContext);

  const {
    api,
    userID: contextUserID,
  } = contextData || {};

  const finalUserID = passedUserID || contextUserID;

  const [modules, setModules] = useState(() =>
    getCachedModules(finalUserID),
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!finalUserID) {
      setModules([]);
      setError("");
      return;
    }

    const cachedModules = getCachedModules(finalUserID);

    setModules(cachedModules);
  }, [finalUserID]);

  const fetchSidebar = useCallback(
    async (forceRefresh = false) => {
      if (!finalUserID) {
        console.error(
          "Sidebar API not called: userid missing",
        );

        setError("User ID not found");

        return null;
      }

      if (!api) {
        console.error(
          "Sidebar API not called: API instance missing",
        );

        setError("API configuration not found");

        return null;
      }

      const cacheKey = `entitlement_${finalUserID}`;

      // ================= CACHE CHECK =================

      if (!forceRefresh) {
        try {
          const cachedData =
            sessionStorage.getItem(cacheKey);

          if (cachedData) {
            const parsedData = JSON.parse(cachedData);

            if (Array.isArray(parsedData)) {
              const sortedData =
                sortModules(parsedData);

              setModules(sortedData);
              setError("");

              return {
                responseCode: "00",
                data: sortedData,
                fromCache: true,
              };
            }

            sessionStorage.removeItem(cacheKey);
          }
        } catch (cacheError) {
          console.error(
            "Entitlement cache parse error:",
            cacheError,
          );

          sessionStorage.removeItem(cacheKey);
        }
      }

      // ================= API CALL =================

      try {
        setLoading(true);
        setError("");

        const payload = {
          userid: finalUserID,
        };

        const response = await api.post(
          "/Entitlement/v1.0/fetchEntitlementModuleDetails",
          payload,
        );

        const responseData = response?.data;

        if (responseData?.responseCode !== "00") {
          const message =
            responseData?.responseMessage ||
            "Unable to fetch sidebar modules";

          console.error(
            "Entitlement API unsuccessful:",
            message,
          );

          setError(message);
          setModules([]);

          return responseData;
        }

        const responseModules =
          responseData?.data ??
          responseData?.response ??
          [];

        const apiModules = Array.isArray(responseModules)
          ? responseModules
          : [];

        const sortedModules =
          sortModules(apiModules);

        sessionStorage.setItem(
          cacheKey,
          JSON.stringify(apiModules),
        );

        sessionStorage.setItem(
          "entitlement",
          JSON.stringify(apiModules),
        );

        setModules(sortedModules);
        setError("");

        return responseData;
      } catch (apiError) {
        console.error(
          "Sidebar API error:",
          apiError?.response?.data || apiError,
        );

        const message =
          apiError?.response?.data?.responseMessage ||
          apiError?.response?.data?.message ||
          apiError?.message ||
          "Sidebar API failed";

        setError(message);

        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, finalUserID],
  );

  // ================= INITIAL API CALL =================

  useEffect(() => {
    if (!finalUserID || !api) {
      console.warn(
        "SIDEBAR API WAITING FOR CONFIG:",
        {
          finalUserID,
          hasApiInstance: Boolean(api),
        },
      );

      return;
    }

    fetchSidebar(false);
  }, [finalUserID, api, fetchSidebar]);

  // ================= FORCE REFRESH =================

  const refreshSidebar = useCallback(async () => {
    if (!finalUserID) {
      console.error(
        "Cannot refresh sidebar: userid missing",
      );

      return null;
    }

    sessionStorage.removeItem(
      `entitlement_${finalUserID}`,
    );

    sessionStorage.removeItem("entitlement");

    return fetchSidebar(true);
  }, [finalUserID, fetchSidebar]);

  return {
    modules,
    loading,
    error,
    refreshSidebar,
  };
};

export default useSideNav;